"use client"

import { useState, useEffect } from "react"
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom"
import { Typography, Row, Col, Card, Button, Select, Pagination, Empty, Breadcrumb, Spin } from "antd"
import { ShoppingCartOutlined, HeartOutlined } from "@ant-design/icons"
import { motion } from "framer-motion"
import { useCart } from "../contexts/CartContext"
import { apiRequest } from "../utils/apiRequest"
import { useCurrency } from "../contexts/CurrencyContext"
import Seo, { siteBase } from "../components/Seo"
import { buildBreadcrumbJsonLd, buildItemListJsonLd, slugifyCategory } from "../lib/seo"
import "../styles/product-cards.css"
import "./ProductListPage.css"

const { Title, Text } = Typography
const { Option } = Select

const CategoryListPage = () => {
  const { category } = useParams()
  const { addToCart } = useCart()
  const navigate = useNavigate()
  const { formatMoney, formatRange } = useCurrency()
  const [sortOrder, setSortOrder] = useState("default")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [total,setTotal] = useState(1)

  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get single parameter
  const id = searchParams.get('provider');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {

    const fetchPopularCategories = async (page = currentPage) => {
      // Only set on mount, never reset for a page/sort change afterwards, so
      // Spin had nothing to show past the first load — every later fetch ran
      // with loading permanently false.
      setLoading(true);
      try {
        let url = `/popular_categories`;
    
        const params = new URLSearchParams();
  
        if(id){params.append('providerId', id)}
  
        if (pageSize && !isNaN(pageSize) && pageSize > 0) {
          params.append('limit', pageSize);
        }
  
        // Add search query if provided
        if (page) {
          params.append('page', page);
        }

        if(sortOrder){
          params.append('sortorder', sortOrder);
        }
  
        // Construct final URL
        url += `?${params.toString()}`;
  
        const response = await apiRequest({
              method: "get",
              url,
            });
        

        const categories = await Promise.all(
          response.data.categories.map(async (category) => {
    
            return {
              id: category.id,
              title: category.name,
              range: (category.min_price != null && category.max_price != null)
                ? formatRange([category.min_price, category.max_price])
                : "You choose your price.",
              icon: category.logo_url,
              path: `/${category.name}`,
              color: 'red',
              asProduct: category.hasProducts ? false : true
            };
          })
        );

        setTotal(response.data.total)
        setCategories(categories)
      } catch (err) {
        setError(err.message);
        console.error('API Error:', err.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularCategories()
  }, [currentPage, sortOrder]);

  const handleSortChange = (value) => {
    setSortOrder(value)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    //fetchPopularCategories(page)
    window.scrollTo(0, 0)
  }

  const sortProducts = (products) => {
    const sorted = [...products]

    switch (sortOrder) {
      case "price-low":
        return sorted.sort((a, b) => {
          const priceA = Array.isArray(a.price) ? a.price[0] : a.price
          const priceB = Array.isArray(b.price) ? b.price[0] : b.price
          return priceA - priceB
        })
      case "price-high":
        return sorted.sort((a, b) => {
          const priceA = Array.isArray(a.price) ? a.price[0] : a.price
          const priceB = Array.isArray(b.price) ? b.price[0] : b.price
          return priceB - priceA
        })
      default:
        return sorted
    }
  }

  //const paginatedProducts = sortProducts(products).slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const formatPrice = (price) => {
    if (Array.isArray(price)) return formatRange(price)
    return formatMoney(price)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
  }

  const getCategoryTitle = () => {
    if (!category) return "Categories"

    return category.charAt(0).toUpperCase() + category.slice(1)
  }

  const pageTitle = getCategoryTitle()
  const canonical = `${siteBase}/categories${category ? `/${category}` : ""}`

  return (
    <div className="product-list-page">
      <Seo
        title={pageTitle === "Categories" ? "Browse Categories" : pageTitle}
        description={
          pageTitle === "Categories"
            ? "Browse every Tanaka Life category — Econet, NetOne and Telecel airtime and data bundles, ZESA tokens and more, delivered instantly to Zimbabwe."
            : `Buy ${pageTitle} instantly with Tanaka Life. Secure payment by card, voucher or instant EFT, delivered to Zimbabwe in seconds.`
        }
        canonical={canonical}
        jsonLd={[
          buildBreadcrumbJsonLd(
            [
              { name: "Home", url: "/" },
              { name: pageTitle, url: `/categories${category ? `/${category}` : ""}` },
            ],
            siteBase,
          ),
          buildItemListJsonLd(
            // Category-as-product entries are bought at /buy/<slug>; browsable
            // categories list their products at /products/<slug>.
            categories.map((c) => ({
              name: c.title,
              url: c.asProduct
                ? `/buy/${slugifyCategory(c.title)}`
                : `/products/${slugifyCategory(c.title)}`,
            })),
            siteBase,
          ),
        ]}
      />
      <div className="product-list-container">
        <Breadcrumb className="breadcrumb">
          <Breadcrumb.Item>
            <Link to="/">Home</Link>
          </Breadcrumb.Item>

          {category && <Breadcrumb.Item>{getCategoryTitle()}</Breadcrumb.Item>}
        </Breadcrumb>

        <div className="product-list-header">
          <Title level={2}>{getCategoryTitle()}</Title>
        </div>

        {/*
          See ProductListPage for the same fix and why: replacing the whole
          grid with a bare spinner on page change collapsed the layout and
          gave no feedback where the user was looking, which read as the click
          not having registered. Spin now overlays the existing cards instead,
          and Pagination is disabled for the same window.
        */}
        <Spin spinning={loading} size="large" tip={categories.length ? "Loading..." : undefined}>
          {categories.length > 0 ? (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <Row gutter={[24, 24]}>
                {categories.map((category, index) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <motion.div variants={itemVariants} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                        <Card
                          hoverable
                          cover={<img src={category.icon} alt={category.title} loading="lazy" />}
                          className="product-card"
                          size="small"
                        >
                          <Card.Meta title={category.title} description={category.range || 'variable'} />
                          {/* Buttons navigate directly rather than being wrapped in a
                              Link: a <button> inside an <a> is invalid markup and
                              leaves two overlapping hit areas for one action. */}
                          <div className="product-card-action">
                            {!category.asProduct ? (
                              <Button
                                type="primary"
                                block
                                className="product-button"
                                onClick={() => navigate(`/products?category=${category.id}`)}
                              >
                                Explore
                              </Button>
                            ) : (
                              <Button
                                type="primary"
                                block
                                className="product-button"
                                onClick={() => navigate(`/buy/${slugifyCategory(category.title)}`)}
                              >
                                Buy Now
                              </Button>
                            )}
                          </div>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}


                  </Row>
            </motion.div>
          ) : loading ? (
            <div className="loading-container" />
          ) : (
            <Empty description="No Categories found" className="empty-products" />
          )}
        </Spin>

        <div className="pagination-container">
          {total > pageSize ? (
            <Pagination
              current={currentPage}
              total={total}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              disabled={loading}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default CategoryListPage

